import { firestore } from 'firebase/app'
import { ICollectionDocument } from './ICollectionDocument'
import { ICollectionMapper } from './ICollectionMapper'

export class FirestoreQuery<T extends ICollectionDocument> {
  protected _collection: firestore.CollectionReference | undefined
  protected _query: firestore.Query | undefined
  protected _resultList: T[] = []
  protected _resultListUnsub: (() => void )| null = null
  protected _resultItem: { data: T | null, unsub: () => void  } = {
    data: null,
    unsub: () => {}
  }
  protected _resultItemUnsub: (() => void )| null = null
  protected _batch: firestore.WriteBatch | undefined

  constructor(protected db: firestore.Firestore, protected mapper: ICollectionMapper<T>) {}

  public collection(collection: string) {
    this._collection = this.db.collection(collection)
    this.setQuery(this._collection)

    return this._collection
  }

  public setQuery(query: firestore.Query) {
    this._query = query
  }

  public setBatch(batch: firestore.WriteBatch) {
    this._batch = batch
  }

  public watchList(cb?: (list: T[], unsub?: () => void) => void): T[] {
    if (this._query === undefined) {
      return []
    }

    const unsub = this._query.onSnapshot(snapshot => {
      snapshot.docChanges().forEach((change) => this.processSnapshotChange(change))
      cb && cb(this._resultList, unsub)
    }, err => {console.log('watchList error', err)})
    this._resultListUnsub = unsub

    return this._resultList
  }

  public watch(
    id: string,
    cb?: (item: T, unsub?: () => void) => void
  ): { data: T | null, unsub: () => void } {

    if (this._collection === undefined) {
      return { data: null, unsub: () => {} }
    }

    const unsub = this._collection.doc(id).onSnapshot(doc => {
      this._resultItem.data = this.mapper.map({ ...doc.data(), id: doc.id })
      cb && cb(this._resultItem.data, unsub)
    }, err => {console.log('watch error', err)})
    this._resultItemUnsub = unsub
    this._resultItem.unsub = this._resultItemUnsub

    return this._resultItem
  }

  async list(): Promise<T[]> {
    if (this._query === undefined) {
      return []
    }

    const snapshot = await this._query.get()
    this._resultList = snapshot.docs.map((doc): T => {
      return this.mapper.map({ ...doc.data(), id: doc.id })
    })

    return this._resultList
  }

  async save(data: T): Promise<T> {
    if (this._collection === undefined) {
      return data
    }

    const parsedData = this.mapper.serialize(data)
    const doc = this._collection.doc(data.id)
    if (this._batch) {
      await this._batch.set(doc, parsedData)
    } else {
      await doc.set(parsedData)
    }

    return data
  }

  async find(id: string): Promise<T | null> {
    if (this._collection === undefined) {
      return null
    }

    const doc = await this._collection.doc(id).get()
    if (!doc.data()) {
      return null
    }

    return this.mapper.map({ ...doc.data(), id: doc.id })
  }

  async delete(data: { id: string }): Promise<void> {
    if (this._collection === undefined) {
      return
    }

    const doc = this._collection.doc(data.id)
    if (this._batch) {
      await this._batch.delete(doc)
    } else {
      await doc.delete()
    }
  }

  async increment(id: string, field: string): Promise<void> {
    if (this._collection === undefined) {
      return
    }

    await this.adjustValue(id, field, 1)
  }

  async decrement(id: string, field: string): Promise<void> {
    if (this._collection === undefined) {
      return
    }

    await this.adjustValue(id, field, -1)
  }

  async adjustValue(id: string, field: string, adjustment: number) {
    if (this._collection === undefined) {
      return
    }

    const update: any = {}
    update[field] = firestore.FieldValue.increment(adjustment)

    await this._collection.doc(id).update(update)
  }

  unsubscribeWatchers() {
    if (typeof this._resultItemUnsub === 'function') {
      this._resultItemUnsub()
    }
    if (typeof this._resultListUnsub === 'function') {
      this._resultListUnsub()
    }
  }

  protected processSnapshotChange(change: firestore.DocumentChange) {
    const object = this.mapper.map({ ...change.doc.data(), id: change.doc.id })

    if (change.type === 'added') {
      this._resultList.push(object)
    } else {
      const index = this._resultList.findIndex((item: T) => item.id === object.id)
      if (index !== -1) {
        if (change.type === 'removed') {
          this._resultList.splice(index, 1)
        } else if (change.type === 'modified') {
          this._resultList.splice(index, 1, object)
        }
      }
    }
  }
}
